import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, Edit2, LogOut, X, Save, Calendar as CalIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ImageUpload from "@/components/ImageUpload";

const TABS = [
  { id: "bouquets", label: "Bouquets" },
  { id: "menu", label: "Menu & Add-ons" },
  { id: "dessert", label: "Dessert of Month" },
  { id: "gallery", label: "Gallery" },
  { id: "testimonials", label: "Testimonials" },
  { id: "calendar", label: "Calendar" },
  { id: "inquiries", label: "Inquiries" },
  { id: "settings", label: "Settings" },
];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="overline block mb-2">{label}</span>
    <input className="elegant-input" {...props} />
  </label>
);
const TextArea = ({ label, ...props }) => (
  <label className="block">
    <span className="overline block mb-2">{label}</span>
    <textarea className="elegant-input resize-none" rows={3} {...props} />
  </label>
);
const Checkbox = ({ label, checked, onChange, ...props }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={onChange} {...props} />
    <span className="text-sm text-ink">{label}</span>
  </label>
);

const ImageField = ({ label, value, onChange, testId = "image" }) => (
  <div>
    <div className="flex items-end justify-between gap-3 mb-2">
      <span className="overline">{label}</span>
      <ImageUpload testId={testId} onUploaded={(url) => onChange({ target: { value: url } })} />
    </div>
    <input className="elegant-input" value={value || ""} onChange={onChange} placeholder="Paste an image URL, or click Upload above" data-testid={`${testId}-url`} />
    {value && (
      <div className="mt-3">
        <img src={value} alt="preview" className="h-24 w-24 object-cover border border-hairline" onError={(e) => { e.target.style.display = "none"; }} />
      </div>
    )}
  </div>
);

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bouquets");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  if (loading || !user) return <div className="min-h-screen bg-ivory" />;

  return (
    <div className="min-h-screen bg-ivory">
      <header className="bg-cream border-b border-hairline">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="font-display text-2xl text-ink">Tammy's Treats</span>
            <span className="overline text-rose">Studio</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="elegant-link text-xs uppercase tracking-[0.2em]" data-testid="admin-view-site">View Site</Link>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="text-xs uppercase tracking-[0.2em] flex items-center gap-2 hover:text-rose" data-testid="admin-logout">
              <LogOut size={14} strokeWidth={1.25} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10 grid md:grid-cols-[220px_1fr] gap-10">
        <aside>
          <nav className="space-y-1 md:sticky md:top-10">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`block w-full text-left px-4 py-3 text-sm border-l-2 transition-colors ${tab === t.id ? "border-rose text-ink bg-cream" : "border-transparent text-ink-soft hover:text-ink"}`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main>
          {tab === "bouquets" && <BouquetsTab />}
          {tab === "menu" && <MenuTab />}
          {tab === "dessert" && <DessertTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "testimonials" && <TestimonialsTab />}
          {tab === "calendar" && <CalendarTab />}
          {tab === "inquiries" && <InquiriesTab />}
          {tab === "settings" && <SettingsTab />}
        </main>
      </div>
    </div>
  );
}

// ============== TABS ==============

const SectionHeader = ({ title, onAdd, addLabel = "Add New" }) => (
  <div className="flex items-center justify-between mb-8 border-b border-hairline pb-5">
    <h2 className="font-display text-3xl text-ink">{title}</h2>
    {onAdd && (
      <button onClick={onAdd} className="btn-rose flex items-center gap-2" data-testid="add-new-btn">
        <Plus size={14} strokeWidth={1.5} /> {addLabel}
      </button>
    )}
  </div>
);

function BouquetsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", description: "", image_url: "", price_range: "", featured: true, display_order: 0 };

  const fetchData = async () => { const { data } = await api.get("/bouquets"); setItems(data); };
  useEffect(() => { fetchData(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/admin/bouquets/${editing.id}`, editing);
    else await api.post("/admin/bouquets", editing);
    setEditing(null); fetchData();
  };
  const del = async (id) => { if (confirm("Delete this bouquet?")) { await api.delete(`/admin/bouquets/${id}`); fetchData(); } };

  return (
    <div>
      <SectionHeader title="Featured Bouquets" onAdd={() => setEditing({ ...empty })} addLabel="Add Bouquet" />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map(b => (
          <div key={b.id} className="border border-hairline bg-ivory" data-testid={`admin-bouquet-${b.id}`}>
            {b.image_url && <img src={b.image_url} className="w-full aspect-[4/5] object-cover" alt={b.name} />}
            <div className="p-5">
              <p className="font-display text-xl text-ink">{b.name}</p>
              <p className="text-sm text-ink-soft mt-1">{b.price_range}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(b)} className="btn-ghost text-[10px] py-2 px-3 flex items-center gap-1"><Edit2 size={12} /> Edit</button>
                <button onClick={() => del(b.id)} className="text-destructive p-2 hover:bg-cream" data-testid={`delete-bouquet-${b.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Bouquet" : "New Bouquet"}>
          <div className="space-y-5">
            <Field label="Name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
            <TextArea label="Description" value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
            <ImageField label="Bouquet Image" testId="bouquet-image" value={editing.image_url} onChange={e => setEditing({ ...editing, image_url: e.target.value })} />
            <Field label="Price Range" value={editing.price_range} placeholder="$65 – $120" onChange={e => setEditing({ ...editing, price_range: e.target.value })} />
            <Field label="Display Order" type="number" value={editing.display_order} onChange={e => setEditing({ ...editing, display_order: parseInt(e.target.value || "0") })} />
            <Checkbox label="Featured" checked={editing.featured} onChange={e => setEditing({ ...editing, featured: e.target.checked })} />
            <button onClick={save} className="btn-rose w-full flex items-center justify-center gap-2" data-testid="save-bouquet"><Save size={14} /> Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function DessertTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dessert-of-month").then(r => setD(r.data)); }, []);
  if (!d) return null;
  const save = async () => { await api.put("/admin/dessert-of-month", d); alert("Saved"); };
  return (
    <div>
      <SectionHeader title="Dessert of the Month" />
      <div className="max-w-2xl space-y-5">
        <Field label="Title" value={d.title} onChange={e => setD({ ...d, title: e.target.value })} />
        <Field label="Subtitle" value={d.subtitle} onChange={e => setD({ ...d, subtitle: e.target.value })} />
        <TextArea label="Description" value={d.description} onChange={e => setD({ ...d, description: e.target.value })} />
        <ImageField label="Dessert of the Month Image" testId="dessert-image" value={d.image_url} onChange={e => setD({ ...d, image_url: e.target.value })} />
        <Field label="Price Range" value={d.price_range} onChange={e => setD({ ...d, price_range: e.target.value })} />
        <button onClick={save} className="btn-rose flex items-center gap-2" data-testid="save-dessert"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { image_url: "", caption: "", category: "gallery", display_order: 0 };
  const fetchData = async () => { const { data } = await api.get("/gallery"); setItems(data); };
  useEffect(() => { fetchData(); }, []);
  const save = async () => {
    if (editing.id) await api.put(`/admin/gallery/${editing.id}`, editing);
    else await api.post("/admin/gallery", editing);
    setEditing(null); fetchData();
  };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/gallery/${id}`); fetchData(); } };
  return (
    <div>
      <SectionHeader title="Gallery & Instagram Feed" onAdd={() => setEditing({ ...empty })} addLabel="Add Image" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map(g => (
          <div key={g.id} className="relative group" data-testid={`gallery-${g.id}`}>
            <img src={g.image_url} className="w-full aspect-square object-cover" alt="" />
            <div className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center gap-2 p-3 text-ivory">
              <p className="text-xs uppercase tracking-wider">{g.category}</p>
              <div className="flex gap-2">
                <button onClick={() => setEditing(g)} className="p-2 bg-ivory text-ink"><Edit2 size={12} /></button>
                <button onClick={() => del(g.id)} className="p-2 bg-destructive text-ivory" data-testid={`delete-gallery-${g.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Image" : "New Image"}>
          <div className="space-y-5">
            <ImageField label="Gallery Image" testId="gallery-image" value={editing.image_url} onChange={e => setEditing({ ...editing, image_url: e.target.value })} />
            <Field label="Caption" value={editing.caption} onChange={e => setEditing({ ...editing, caption: e.target.value })} />
            <label className="block">
              <span className="overline block mb-2">Category</span>
              <select className="elegant-input" value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })}>
                <option value="gallery">Main Gallery</option>
                <option value="instagram">Instagram Feed</option>
              </select>
            </label>
            <Field label="Display Order" type="number" value={editing.display_order} onChange={e => setEditing({ ...editing, display_order: parseInt(e.target.value || "0") })} />
            <button onClick={save} className="btn-rose w-full" data-testid="save-gallery">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const empty = { customer_name: "", event_type: "", quote: "", photo_url: "", rating: 5, approved: true, display_order: 0 };
  const fetchData = async () => { const { data } = await api.get("/admin/testimonials"); setItems(data); };
  useEffect(() => { fetchData(); }, []);
  const save = async () => {
    if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, editing);
    else await api.post("/admin/testimonials", editing);
    setEditing(null); fetchData();
  };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/testimonials/${id}`); fetchData(); } };

  return (
    <div>
      <SectionHeader title="Testimonials" onAdd={() => setEditing({ ...empty })} addLabel="Add Review" />
      <p className="text-sm text-ink-soft mb-6 font-light italic">Customer-submitted reviews post directly to the site. Use the edit button to hide or refine any review.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {items.map(t => (
          <div key={t.id} className={`border p-6 ${t.approved ? "border-hairline bg-ivory" : "border-hairline bg-cream opacity-70"}`} data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex gap-1 mb-3">
              {Array.from({ length: t.rating || 5 }).map((_, k) => (
                <span key={k} className="text-rose">★</span>
              ))}
            </div>
            <p className="font-display italic text-ink leading-relaxed">"{t.quote}"</p>
            <div className="mt-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-ink">{t.customer_name}</p>
                <p className="overline text-rose mt-1">{t.event_type || "General"}{!t.approved && " · Hidden"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(t)} className="p-2 hover:bg-cream" data-testid={`edit-testimonial-${t.id}`}><Edit2 size={14} /></button>
                <button onClick={() => del(t.id)} className="p-2 text-destructive hover:bg-cream" data-testid={`delete-testimonial-${t.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-soft text-sm">No testimonials yet.</p>}
      </div>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Testimonial" : "New Testimonial"}>
          <div className="space-y-5">
            <Field label="Customer Name" value={editing.customer_name} onChange={e => setEditing({ ...editing, customer_name: e.target.value })} />
            <Field label="Event Type" value={editing.event_type} onChange={e => setEditing({ ...editing, event_type: e.target.value })} placeholder="Wedding · Birthday · etc." />
            <TextArea label="Quote" value={editing.quote} onChange={e => setEditing({ ...editing, quote: e.target.value })} />
            <ImageField label="Customer Photo (optional)" testId="testimonial-photo" value={editing.photo_url} onChange={e => setEditing({ ...editing, photo_url: e.target.value })} />
            <Field label="Rating (1–5)" type="number" min="1" max="5" value={editing.rating} onChange={e => setEditing({ ...editing, rating: parseInt(e.target.value || "5") })} />
            <Field label="Display Order" type="number" value={editing.display_order} onChange={e => setEditing({ ...editing, display_order: parseInt(e.target.value || "0") })} />
            <Checkbox label="Visible on site (uncheck to hide)" checked={editing.approved !== false} onChange={e => setEditing({ ...editing, approved: e.target.checked })} />
            <button onClick={save} className="btn-rose w-full" data-testid="save-testimonial">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CalendarTab() {
  const [items, setItems] = useState([]);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("Fully Booked");
  const fetchData = async () => { const { data } = await api.get("/blocked-dates"); setItems(data); };
  useEffect(() => { fetchData(); }, []);
  const add = async () => {
    if (!date) return;
    await api.post("/admin/blocked-dates", { date, reason });
    setDate(""); fetchData();
  };
  const del = async (id) => { await api.delete(`/admin/blocked-dates/${id}`); fetchData(); };
  return (
    <div>
      <SectionHeader title="Order Availability Calendar" />
      <div className="bg-cream border border-hairline p-8 mb-8 max-w-2xl">
        <p className="overline mb-4 flex items-center gap-2"><CalIcon size={14} strokeWidth={1.25} className="text-rose" /> Block a Date</p>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <Field label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} />
          <label className="block">
            <span className="overline block mb-2">Reason</span>
            <select className="elegant-input" value={reason} onChange={e => setReason(e.target.value)}>
              <option>Fully Booked</option>
              <option>Holiday Closure</option>
              <option>Unavailable</option>
            </select>
          </label>
          <button onClick={add} className="btn-rose" data-testid="block-date-btn">Block Date</button>
        </div>
      </div>
      <div className="space-y-2">
        {items.map(b => (
          <div key={b.id} className="flex items-center justify-between border-b border-hairline py-3" data-testid={`blocked-date-${b.date}`}>
            <div>
              <p className="font-display text-lg text-ink">{b.date}</p>
              <p className="overline text-rose mt-1">{b.reason}</p>
            </div>
            <button onClick={() => del(b.id)} className="text-destructive p-2 hover:bg-cream"><Trash2 size={14} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-soft text-sm">No dates blocked yet.</p>}
      </div>
    </div>
  );
}

function InquiriesTab() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const fetchData = async () => { const { data } = await api.get("/admin/inquiries"); setItems(data); };
  useEffect(() => { fetchData(); }, []);
  const setStatus = async (id, status) => { await api.put(`/admin/inquiries/${id}/status?status=${status}`); fetchData(); };
  const del = async (id) => { if (confirm("Delete?")) { await api.delete(`/admin/inquiries/${id}`); fetchData(); } };
  return (
    <div>
      <SectionHeader title="Customer Inquiries" />
      <div className="space-y-3">
        {items.map(i => (
          <div key={i.id} className="border border-hairline bg-ivory" data-testid={`inquiry-${i.id}`}>
            <div className="p-5 flex flex-wrap items-center justify-between gap-3 cursor-pointer" onClick={() => setExpanded(expanded === i.id ? null : i.id)}>
              <div>
                <p className="font-display text-xl text-ink">{i.customer_name}</p>
                <p className="text-xs text-ink-soft mt-1">{i.occasion} · {i.event_date} · {i.product}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] uppercase tracking-wider px-3 py-1 ${i.status === "new" ? "bg-rose text-ivory" : i.status === "confirmed" ? "bg-sage text-ivory" : "bg-cream text-ink"}`}>{i.status}</span>
                <p className="text-xs text-ink-soft">{new Date(i.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            {expanded === i.id && (
              <div className="border-t border-hairline p-5 space-y-3 text-sm bg-cream">
                <p><span className="overline">Email:</span> <a href={`mailto:${i.customer_email}`} className="elegant-link ml-2">{i.customer_email}</a></p>
                <p><span className="overline">Phone:</span> <span className="ml-2">{i.customer_phone || "—"}</span></p>
                <p><span className="overline">Size:</span> <span className="ml-2">{i.size}</span></p>
                <p><span className="overline">Cake Flavor:</span> <span className="ml-2">{i.cake_flavor || (i.flavors?.find(f => f.startsWith("Cake:"))?.replace("Cake: ", "")) || "—"}</span></p>
                <p><span className="overline">Fillings:</span> <span className="ml-2">{(i.filling_flavors?.length ? i.filling_flavors.join(", ") : (i.flavors?.find(f => f.startsWith("Filling:"))?.replace("Filling: ", ""))) || "—"}</span></p>
                <p><span className="overline">Packaging:</span> <span className="ml-2">{i.gift_box === "clear_box" ? "Clear Gift Box (add-on)" : "Standard"}</span></p>
                <p><span className="overline">Add-ons:</span> <span className="ml-2">{i.selected_add_ons?.length ? i.selected_add_ons.join(", ") : "—"}</span></p>
                <p><span className="overline">Colors:</span> <span className="ml-2">{i.custom_colors || "—"}</span></p>
                <p><span className="overline">Delivery:</span> <span className="ml-2">{i.delivery_type} {i.delivery_address && `— ${i.delivery_address}`}</span></p>
                {i.notes && <p><span className="overline">Notes:</span> <span className="ml-2">{i.notes}</span></p>}
                {i.inspiration_urls?.length > 0 && (
                  <div>
                    <span className="overline">Inspiration:</span>
                    <ul className="ml-2 mt-2 space-y-1">
                      {i.inspiration_urls.map((u, k) => <li key={k}><a href={u} target="_blank" rel="noopener noreferrer" className="elegant-link break-all">{u}</a></li>)}
                    </ul>
                  </div>
                )}
                <div className="flex gap-2 pt-4 border-t border-hairline">
                  <button onClick={() => setStatus(i.id, "reviewed")} className="btn-ghost text-[10px] py-2 px-3">Mark Reviewed</button>
                  <button onClick={() => setStatus(i.id, "confirmed")} className="btn-ghost text-[10px] py-2 px-3">Confirm</button>
                  <button onClick={() => setStatus(i.id, "declined")} className="btn-ghost text-[10px] py-2 px-3">Decline</button>
                  <button onClick={() => del(i.id)} className="ml-auto p-2 text-destructive hover:bg-ivory" data-testid={`delete-inquiry-${i.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-soft text-sm">No inquiries yet.</p>}
      </div>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => { await api.put("/admin/settings", s); alert("Settings saved"); };
  const set = (k, v) => setS({ ...s, [k]: v });
  return (
    <div>
      <SectionHeader title="Site Settings" />
      <div className="max-w-3xl space-y-12">
        <section>
          <p className="overline text-rose mb-5">Brand</p>
          <div className="space-y-5">
            <Field label="Business Name" value={s.business_name} onChange={e => set("business_name", e.target.value)} />
            <ImageField label="Logo" testId="logo-upload" value={s.logo_url} onChange={e => set("logo_url", e.target.value)} />
          </div>
        </section>

        <section>
          <p className="overline text-rose mb-5">Hero</p>
          <div className="space-y-5">
            <TextArea label="Hero Title (use \n for line break)" value={s.hero_title} onChange={e => set("hero_title", e.target.value)} />
            <TextArea label="Hero Subtitle" value={s.hero_subtitle} onChange={e => set("hero_subtitle", e.target.value)} />
            <ImageField label="Hero Background Image" testId="hero-upload" value={s.hero_image_url} onChange={e => set("hero_image_url", e.target.value)} />
          </div>
        </section>

        <section>
          <p className="overline text-rose mb-5">About</p>
          <div className="space-y-5">
            <TextArea label="About Text" value={s.about_text} onChange={e => set("about_text", e.target.value)} />
            <ImageField label="About Image" testId="about-upload" value={s.about_image_url} onChange={e => set("about_image_url", e.target.value)} />
          </div>
        </section>

        <section>
          <p className="overline text-rose mb-5">Contact</p>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Phone" value={s.phone} onChange={e => set("phone", e.target.value)} />
            <Field label="Email" value={s.email} onChange={e => set("email", e.target.value)} />
            <Field label="Location" value={s.location} onChange={e => set("location", e.target.value)} />
          </div>
        </section>

        <section>
          <p className="overline text-rose mb-5">Social Media</p>
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Instagram Handle" value={s.instagram_handle} onChange={e => set("instagram_handle", e.target.value)} />
            <Field label="Instagram URL" value={s.instagram_url} onChange={e => set("instagram_url", e.target.value)} />
            <Field label="Facebook Handle" value={s.facebook_handle} onChange={e => set("facebook_handle", e.target.value)} />
            <Field label="Facebook URL" value={s.facebook_url} onChange={e => set("facebook_url", e.target.value)} />
            <Field label="TikTok Handle" value={s.tiktok_handle} onChange={e => set("tiktok_handle", e.target.value)} />
            <Field label="TikTok URL" value={s.tiktok_url} onChange={e => set("tiktok_url", e.target.value)} />
          </div>
        </section>

        <section>
          <p className="overline text-rose mb-5">Venmo Payment</p>
          <div className="space-y-5">
            <Checkbox label="Show Venmo Payment Option on Site" checked={s.venmo_enabled} onChange={e => set("venmo_enabled", e.target.checked)} data-testid="venmo-enabled" />
            <Field label="Venmo Handle" value={s.venmo_handle} onChange={e => set("venmo_handle", e.target.value)} placeholder="@Tammy-Takacs" />
            <Field label="Venmo URL" value={s.venmo_url} onChange={e => set("venmo_url", e.target.value)} placeholder="https://venmo.com/u/Tammy-Takacs" />
          </div>
        </section>

        <section>
          <p className="overline text-rose mb-5">Delivery & Pickup</p>
          <div className="space-y-5">
            <Checkbox label="Delivery Enabled" checked={s.delivery_enabled} onChange={e => set("delivery_enabled", e.target.checked)} data-testid="delivery-enabled" />
            <Field label="Delivery Radius (miles)" type="number" value={s.delivery_radius_miles} onChange={e => set("delivery_radius_miles", parseInt(e.target.value || "0"))} />
            <TextArea label="Delivery Fee Description" value={s.delivery_fee} onChange={e => set("delivery_fee", e.target.value)} />
            <TextArea label="Pickup Instructions" value={s.pickup_instructions} onChange={e => set("pickup_instructions", e.target.value)} />
            <Field label="Pickup Hours" value={s.pickup_hours} onChange={e => set("pickup_hours", e.target.value)} />
            <Field label="Max Orders Per Day" type="number" value={s.max_orders_per_day} onChange={e => set("max_orders_per_day", parseInt(e.target.value || "0"))} />
          </div>
        </section>

        <button onClick={save} className="btn-rose flex items-center gap-2" data-testid="save-settings"><Save size={14} /> Save Settings</button>
      </div>
    </div>
  );
}


function MenuTab() {
  const [addOns, setAddOns] = useState([]);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [flavorDraft, setFlavorDraft] = useState(null); // {cake_flavors, premium_flavors, filling_flavors}
  const empty = { name: "", description: "", price: "", estimated_price: 0, image_url: "", display_order: 0, active: true };

  const fetchAll = async () => {
    const [a, s] = await Promise.all([api.get("/admin/add-ons"), api.get("/settings")]);
    setAddOns(a.data);
    setSettings(s.data);
    setFlavorDraft({
      cake_flavors: (s.data.cake_flavors || []).join("\n"),
      premium_flavors: (s.data.premium_flavors || []).join("\n"),
      filling_flavors: (s.data.filling_flavors || []).join("\n"),
    });
  };
  useEffect(() => { fetchAll(); }, []);

  const saveAddOn = async () => {
    if (editing.id) await api.put(`/admin/add-ons/${editing.id}`, editing);
    else await api.post("/admin/add-ons", editing);
    setEditing(null); fetchAll();
  };
  const delAddOn = async (id) => { if (confirm("Delete this add-on?")) { await api.delete(`/admin/add-ons/${id}`); fetchAll(); } };

  const saveFlavors = async () => {
    const toList = (s) => s.split("\n").map(x => x.trim()).filter(Boolean);
    const next = {
      ...settings,
      cake_flavors: toList(flavorDraft.cake_flavors),
      premium_flavors: toList(flavorDraft.premium_flavors),
      filling_flavors: toList(flavorDraft.filling_flavors),
    };
    await api.put("/admin/settings", next);
    alert("Menu flavors saved");
    fetchAll();
  };

  if (!settings) return null;

  return (
    <div>
      <SectionHeader title="Menu & Add-ons" onAdd={() => setEditing({ ...empty })} addLabel="Add Add-on" />

      {/* Add-ons */}
      <p className="overline text-rose mb-5">Add-ons</p>
      <p className="text-sm text-ink-soft mb-6 font-light italic">Extras customers can attach to any inquiry. Show on the Menu page and the inquiry Size step.</p>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
        {addOns.map(a => (
          <div key={a.id} className={`border p-6 ${a.active ? "border-hairline bg-ivory" : "border-hairline bg-cream opacity-60"}`} data-testid={`admin-addon-${a.id}`}>
            {a.image_url && <img src={a.image_url} alt={a.name} className="w-full aspect-square object-cover mb-4" />}
            <p className="font-script text-2xl text-rose leading-none">{a.name}</p>
            <p className="text-sm text-ink-soft mt-2 font-light">{a.description}</p>
            {a.price && <p className="font-display text-lg text-ink mt-3">{a.price}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditing(a)} className="btn-ghost text-[10px] py-2 px-3 flex items-center gap-1" data-testid={`edit-addon-${a.id}`}><Edit2 size={12} /> Edit</button>
              <button onClick={() => delAddOn(a.id)} className="text-destructive p-2 hover:bg-cream" data-testid={`delete-addon-${a.id}`}><Trash2 size={14} /></button>
              {!a.active && <span className="ml-auto overline text-ink-soft">Hidden</span>}
            </div>
          </div>
        ))}
        {addOns.length === 0 && <p className="text-ink-soft text-sm col-span-full">No add-ons yet.</p>}
      </div>

      {/* Flavor lists */}
      <p className="overline text-rose mb-5">Menu Flavors</p>
      <p className="text-sm text-ink-soft mb-6 font-light italic">One flavor per line. Changes appear on the public Menu page and the inquiry Flavors step.</p>
      <div className="grid md:grid-cols-3 gap-5 max-w-4xl">
        <div>
          <span className="overline block mb-2">Cake Flavors</span>
          <textarea className="elegant-input resize-none" rows={6} data-testid="flavors-cake" value={flavorDraft?.cake_flavors || ""} onChange={e => setFlavorDraft({ ...flavorDraft, cake_flavors: e.target.value })} />
        </div>
        <div>
          <span className="overline block mb-2">Premium Flavors <span className="text-ink-soft normal-case tracking-normal text-[10px]">(+$2/6pc)</span></span>
          <textarea className="elegant-input resize-none" rows={6} data-testid="flavors-premium" value={flavorDraft?.premium_flavors || ""} onChange={e => setFlavorDraft({ ...flavorDraft, premium_flavors: e.target.value })} />
        </div>
        <div>
          <span className="overline block mb-2">Filling Flavors</span>
          <textarea className="elegant-input resize-none" rows={6} data-testid="flavors-filling" value={flavorDraft?.filling_flavors || ""} onChange={e => setFlavorDraft({ ...flavorDraft, filling_flavors: e.target.value })} />
        </div>
      </div>
      <button onClick={saveFlavors} className="btn-rose mt-6 flex items-center gap-2" data-testid="save-flavors"><Save size={14} /> Save Flavor Lists</button>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit Add-on" : "New Add-on"}>
          <div className="space-y-5">
            <Field label="Name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
            <TextArea label="Description" value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} />
            <Field label="Display Price" value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} placeholder="$5  or  $2 – $10" />
            <Field label="Estimated Price (numeric, for internal email estimate)" type="number" step="0.01" value={editing.estimated_price} onChange={e => setEditing({ ...editing, estimated_price: parseFloat(e.target.value || "0") })} />
            <ImageField label="Image (optional)" testId="addon-image" value={editing.image_url} onChange={e => setEditing({ ...editing, image_url: e.target.value })} />
            <Field label="Display Order" type="number" value={editing.display_order} onChange={e => setEditing({ ...editing, display_order: parseInt(e.target.value || "0") })} />
            <Checkbox label="Visible on site" checked={editing.active !== false} onChange={e => setEditing({ ...editing, active: e.target.checked })} />
            <button onClick={saveAddOn} className="btn-rose w-full flex items-center justify-center gap-2" data-testid="save-addon"><Save size={14} /> Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-10 overflow-y-auto">
      <div className="bg-ivory border border-hairline w-full max-w-xl p-8 md:p-10 my-auto" data-testid="admin-modal">
        <div className="flex items-center justify-between mb-8 border-b border-hairline pb-4">
          <h3 className="font-display text-2xl text-ink">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-cream" data-testid="modal-close"><X size={18} strokeWidth={1.25} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
